import { PARTNER_ID_RSM_FAO, PARTNER_ID_RSM_VAR, PARTNER_ID_WTI_GLOBAL } from '@Shared/constants/partners';

export enum STAGE {
  EMAIL_VERIFICATION = 'EMAIL_VERIFICATION',
  COMPANY_INFORMATION = 'COMPANY_INFORMATION',
  PERSONAL_INFORMATION = 'PERSONAL_INFORMATION',
  REFERRAL_SCREEN = 'REFERRAL_SCREEN', // special case,
  PLAN_SELECTION = 'PLAN_SELECTION',
  IMPLEMENTATION_OPTIONS = 'IMPLEMENTATION_OPTIONS',
  EMBURSE_CARDS = 'EMBURSE_CARDS',
  RSM_PARTNER_REFERRAL_SCREEN = 'RSM_PARTNER_REFERRAL_SCREEN',
  BUSINESS_VERIFICATION = 'BUSINESS_VERIFICATION',
  SUBMIT_APPLICATION = 'SUBMIT_APPLICATION',
  PAYMENT_SUMMARY = 'PAYMENT_SUMMARY',
  FINISH = 'FINISH'
}

export type STEP = Extract<
  STAGE,
  | STAGE.COMPANY_INFORMATION
  | STAGE.PERSONAL_INFORMATION
  | STAGE.PLAN_SELECTION
  | STAGE.EMBURSE_CARDS
  | STAGE.RSM_PARTNER_REFERRAL_SCREEN
  | STAGE.FINISH
>;

type Step = {
  id: STEP;
  stages: STAGE[];
  label?: string;
  content?: string[];
};

const _NEW_COMPANY_REQUIRED_STEPS: Step[] = [
  {
    label: 'Company Information',
    content: ['Provide basic account information'],
    stages: [STAGE.COMPANY_INFORMATION],
    id: STAGE.COMPANY_INFORMATION
  },
  {
    label: 'Personal Information',
    content: ['Provide personal details'],
    stages: [STAGE.PERSONAL_INFORMATION],
    id: STAGE.PERSONAL_INFORMATION
  }
];

const _ADDITIONAL_COMPANY_REQUIRED_STEPS = _NEW_COMPANY_REQUIRED_STEPS.filter(({ id }) => id === STAGE.COMPANY_INFORMATION);

function getPlanSelectionStep(isWtiPartner?: boolean): Step {
  return {
    label: 'Plan Selection',
    content: isWtiPartner ? ['Select your plan'] : ['Select your plan', 'Implement options'],
    stages: isWtiPartner ? [STAGE.PLAN_SELECTION] : [STAGE.PLAN_SELECTION, STAGE.IMPLEMENTATION_OPTIONS],
    id: STAGE.PLAN_SELECTION
  };
}

function getCardsStep(isWtiPartner?: boolean): Step {
  return {
    label: 'Emburse Cards',
    content: isWtiPartner ? ['Apply for Cards'] : ['Apply for Cards', 'Business verification', 'Submit Application'],
    stages: isWtiPartner ? [STAGE.EMBURSE_CARDS] : [STAGE.EMBURSE_CARDS, STAGE.BUSINESS_VERIFICATION, STAGE.SUBMIT_APPLICATION],
    id: STAGE.EMBURSE_CARDS
  };
}

function getFinishStep(addPayment: boolean): Step {
  return addPayment
    ? {
        label: 'Finish',
        content: ['Review and Pay', 'Start using Emburse Spend'],
        stages: [STAGE.PAYMENT_SUMMARY, STAGE.FINISH],
        id: STAGE.FINISH
      }
    : {
        label: 'Finish',
        content: ['Start using Emburse Spend'],
        stages: [STAGE.FINISH],
        id: STAGE.FINISH
      };
}

export function getAvailableStepsData(
  isFromSales: boolean,
  cards: string,
  skipReferralCards?: boolean,
  isAdditionalCompany?: boolean,
  partnerId?: string
) {
  const isRsmPartner = [PARTNER_ID_RSM_FAO, PARTNER_ID_RSM_VAR].includes(partnerId || '');
  const isWtiPartner = partnerId === PARTNER_ID_WTI_GLOBAL;
  const isInternalBilling =
    window.FEATURES.stripe_billing || (!window.FEATURES.external_billing && !window.ORG?.is_manual_billing_contract);
  const showPayment = !isRsmPartner && !isFromSales && !isWtiPartner && isInternalBilling;
  const steps = !isAdditionalCompany ? [..._NEW_COMPANY_REQUIRED_STEPS] : [..._ADDITIONAL_COMPANY_REQUIRED_STEPS];
  let optionalSteps = {
    addCards: true,
    addPayment: showPayment,
    planSelection: true
  };

  if (isRsmPartner || isAdditionalCompany || isWtiPartner) {
    optionalSteps.planSelection = false;
  }

  if ((cards === 'hide' && !skipReferralCards) || isAdditionalCompany) {
    optionalSteps.addCards = false;
  }

  if (optionalSteps.planSelection) {
    steps.push(getPlanSelectionStep(isWtiPartner));
  }

  if (optionalSteps.addCards) {
    steps.push(getCardsStep(isWtiPartner));
  }

  if (isRsmPartner) {
    steps.push({
      stages: [STAGE.RSM_PARTNER_REFERRAL_SCREEN],
      id: STAGE.RSM_PARTNER_REFERRAL_SCREEN
    });
  }

  steps.push(getFinishStep(optionalSteps.addPayment));

  let stageToStepMapping = steps.reduce((acc: Record<string, number>, step: any, index: number) => {
    if (isFromSales && step.id === STAGE.PLAN_SELECTION) {
      return acc;
    }
    if (skipReferralCards && step.id === STAGE.EMBURSE_CARDS) {
      return acc;
    }
    const stages = step.stages;
    stages.forEach((stage: number) => {
      acc[stage] = index;
    });
    return acc;
  }, {});

  if (isFromSales) {
    let idToFind = STAGE.EMBURSE_CARDS;
    if (skipReferralCards) {
      idToFind = STAGE.FINISH;
    }
    const index = steps.findIndex((step) => step.id === idToFind);
    stageToStepMapping[STAGE.REFERRAL_SCREEN] = index;
  }

  return {
    steps,
    stageToStepMapping
  };
}
